import { useState, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { IconNameEnum } from "@b2bportal/core-themes";
import { ensureExhaustive, useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  ShopMode,
  useAvailabilityPageTracking,
  useHotelsAvailability,
} from "@tenant/hotels";
import { useControlledDialog } from "@tenant/ui";
import { HotelsAvailabilitySelectors } from "../../../store";

export const useAvailabilityPageContainer = () => {
  const { context } = useHotelsAvailability();

  const isInitializing = useSelector(
    HotelsAvailabilitySelectors.isInitializingValues
  );

  const { hotelsList, isLoading, isLoadingNext, isError } = context;

  const [hoveredId, setHoveredId] = useState<string | undefined>(undefined);
  const { isDesktopAndUp } = useDeviceType();
  const [shopMode, setShopMode] = useState<ShopMode>(ShopMode.LIST);

  const { t } = useI18nContext();

  const centroid = useSelector(HotelsAvailabilitySelectors.getCentroid);
  const zoom = useSelector(HotelsAvailabilitySelectors.getMapZoom);

  const onMapClose = useCallback(() => {
    setShopMode(ShopMode.LIST);
  }, []);

  const mapDialog = useControlledDialog({
    onCloseCallback: () => {
      onMapClose();
    },
  });

  const shopModeConfig = useMemo(() => {
    switch (shopMode) {
      case ShopMode.LIST: {
        return {
          icon: IconNameEnum.map,
          text: t("core-hotels.availabilityPage.shopModeButton.map"),
          onClick: () => {
            setShopMode(ShopMode.MAP);
            mapDialog.setOpen(true);
          },
        };
      }
      case ShopMode.MAP: {
        return {
          className: "map",
          icon: IconNameEnum.list,
          text: t("core-hotels.availabilityPage.shopModeButton.list"),
          onClick: () => {
            setShopMode(ShopMode.LIST);
            onMapClose();
          },
        };
      }
      default:
        ensureExhaustive(shopMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopMode]);

  const isReady = useMemo(
    () => !isLoading && !isInitializing,
    [isLoading, isInitializing]
  );

  useAvailabilityPageTracking({
    shopMode,
    isReady,
  });

  const showMap = shopMode === ShopMode.MAP || isDesktopAndUp;
  const showList = shopMode === ShopMode.LIST || isDesktopAndUp;

  return {
    context: {
      isLoading,
      isInitializing,
      isError,
      hotelsList,
      shopMode,
      hoveredId,
      showMap,
      showList,
      mapCenter: centroid,
      shopModeConfig,
      zoom,
      isReady,
      isLoadingNext,
      mapDialog,
    },
    handlers: {
      setHoveredId,
    },
  };
};
